import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { Award, AwardChip } from "src/grading/components/AwardChip/AwardChip";
import { FilterPills } from "src/grading/components/FilterPills/FilterPills";
import { InlineAlert } from "src/grading/components/InlineAlert/InlineAlert";
import { SideDrawer } from "src/grading/components/SideDrawer/SideDrawer";
import { SourceBadge } from "src/grading/components/SourceBadge/SourceBadge";
import { StatusChip } from "src/grading/components/StatusChip/StatusChip";
import type { ApiError, RosterStudent } from "src/models";
import type {
  Accommodation,
  HealthFlag,
  ModificationLevel,
} from "src/models/hierarchy";
import { EVENT_LABEL, EventId } from "src/models/sessions";
import {
  useGetAccommodationQuery,
  useSetAccommodationMutation,
} from "src/store/reducers/classes/classesApiSlice";

/** Plan ladder, health flags and alternative events: same lists as `AccommodationsPanel`. */
const LEVELS: ModificationLevel[] = [
  "none",
  "modification",
  "adaptation",
  "alternative",
];
const LEVEL_SHORT: Record<ModificationLevel, string> = {
  none: "None",
  modification: "Modification",
  adaptation: "Adaptation",
  alternative: "Alternative test",
};
const FLAGS: HealthFlag[] = ["asthma", "cardiac", "injury", "other"];
const FLAG_LABEL: Record<HealthFlag, string> = {
  asthma: "Asthma",
  cardiac: "Cardiac",
  injury: "Injury",
  other: "Other (see notes)",
};
const ALTERNATIVES: EventId[] = [
  "pft-beep-15m",
  "pft-plank",
  "pft-pullups",
  "pft-mile",
];

/**
 * Roster student. `source` and `externalId` may be unknown (e.g. built from a session's roster
 * snapshot); the drawer then shows "—" instead of guessing.
 */
export type StudentRecordStudent = Omit<
  RosterStudent,
  "source" | "externalId"
> & {
  source?: RosterStudent["source"];
  externalId?: string | null;
};

export interface StudentRecordHistoryItem {
  /** Window label such as "Fall 2026" (season, then year). */
  window: string;
  tier: Award;
}

export interface StudentRecordDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Roster student, the shape the class page passes to `AccommodationsPanel`. */
  student: StudentRecordStudent | null;
  /** Class shown under the name, e.g. "Period 3 PE". */
  classLabel: string;
  /** Class id, passed to the accommodation mutation for cache invalidation. */
  classId?: string;
  /** Age on test day when the caller knows it. */
  ageOnTestDay?: number | null;
  /**
   * Test date (YYYY-MM-DD). With it, the field reads "Age on test day", computed from the
   * birth date at that date; without it (and without `ageOnTestDay`) the field reads "Age".
   */
  testDate?: string;
  /**
   * Finalized windows, newest first; `window` labels look like "Fall 2026". The first item
   * is the one "Open {window} result →" refers to.
   */
  history?: StudentRecordHistoryItem[];
  /**
   * Shows the accommodation plan and the health note (staff with the read-accommodations
   * permission, constitution Principle IV). Implied by `canEdit`. Default false: neither is rendered.
   */
  canViewHealth?: boolean;
  /** Allows editing and saving the plan; implies `canViewHealth`. */
  canEdit?: boolean;
  onSaved?: (accommodation: Accommodation) => void;
  /**
   * Fields the district's roster owns (`StudentDetail.readOnlyFields`). Each such field shows the
   * Roster badge, and the drawer explains where to change them (FR-022).
   */
  readOnlyFields?: readonly string[];
  /** Opens the most recent result; the link is hidden without it. */
  onOpenResult?: () => void;
}

const messageOf = (e: unknown, fallback: string) => {
  const message = (e as ApiError | undefined)?.data?.message;
  return Array.isArray(message) ? message.join(", ") : (message ?? fallback);
};

type DateParts = { year: number; month: number; day: number };

/** Reads the calendar date of "YYYY-MM-DD..." by hand (`new Date` would parse it as UTC). */
const dateParts = (value: string | null | undefined): DateParts | null => {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  return match
    ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
    : null;
};

const today = (): DateParts => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };
};

/** Whole years from `birthDate` to `on`. */
export const ageOn = (
  birthDate: string | null,
  on: DateParts,
): number | null => {
  const born = dateParts(birthDate);
  if (!born) return null;
  const beforeBirthday =
    on.month < born.month || (on.month === born.month && on.day < born.day);
  return on.year - born.year - (beforeBirthday ? 1 : 0);
};

const initialsOf = (firstName: string, lastName: string) =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const Field = ({
  label,
  children,
  roster = false,
}: {
  label: string;
  children: ReactNode;
  /** The roster owns this value: show the Roster badge beside the label. */
  roster?: boolean;
}) => (
  <Box
    sx={(theme) => ({
      px: 1.75,
      py: 1.5,
      borderRadius: `${radius.control}px`,
      bgcolor: theme.palette.surfaceAlt,
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      minWidth: 0,
    })}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Typography
        component="span"
        sx={(theme) => ({
          fontSize: 11,
          fontWeight: 700,
          color: theme.palette.tokens.textMuted,
        })}
      >
        {label}
      </Typography>
      {roster ? <SourceBadge source="roster" /> : null}
    </Box>
    <Box
      component="span"
      sx={{ fontSize: 15, fontWeight: 800, overflowWrap: "anywhere" }}
    >
      {children}
    </Box>
  </Box>
);

const SectionHeading = ({ children }: { children: ReactNode }) => (
  <Typography
    component="h3"
    sx={{
      m: 0,
      fontSize: 14,
      fontWeight: 900,
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

/**
 * Student record (prototype `hasDrawer`): roster facts, the accommodation plan (staff with
 * accommodation access), health note and testing history. Saves through the same
 * `setAccommodation` mutation and payload as `AccommodationsPanel`.
 */
export const StudentRecordDrawer = ({
  open,
  onClose,
  student,
  classLabel,
  classId,
  ageOnTestDay,
  testDate,
  history = [],
  canViewHealth = false,
  canEdit = false,
  onSaved,
  readOnlyFields,
  onOpenResult,
}: StudentRecordDrawerProps) => {
  const { t } = useTranslation();
  const showHealth = canEdit || canViewHealth;
  const {
    data,
    isSuccess: loaded,
    isError: loadFailed,
  } = useGetAccommodationQuery(student?.id ?? "", {
    skip: !student || !open || !showHealth,
  });
  const [save, { isLoading: saving }] = useSetAccommodationMutation();
  const [level, setLevel] = useState<ModificationLevel>("none");
  const [eventId, setEventId] = useState("");
  const [plannedNote, setPlannedNote] = useState("");
  const [notes, setNotes] = useState("");
  const [flags, setFlags] = useState<HealthFlag[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Latest snapshot, read by the reset effect without making it a dependency: a parent
  // re-render that rebuilds the same student must not wipe unsaved edits.
  const studentRef = useRef(student);
  studentRef.current = student;
  const studentId = student?.id;

  useEffect(() => {
    const current = studentRef.current;
    if (!studentId || !current || !open) return;
    // Editing seeds only from the fetched plan, never the snapshot, so a save cannot
    // overwrite the stored health note with stale or missing data.
    const a = canEdit
      ? loaded
        ? data
        : null
      : (data ?? current.accommodation);
    setLevel((a?.plannedModification as ModificationLevel) ?? "none");
    setEventId(a?.plannedEventId ?? "");
    setPlannedNote(a?.plannedNote ?? "");
    setNotes(a?.notes ?? "");
    setFlags(
      ((a?.healthFlags ?? []) as HealthFlag[]).filter((f) => FLAGS.includes(f)),
    );
    setError(null);
  }, [data, studentId, open, canEdit, loaded]);

  if (!student) return null;

  const name = `${student.firstName} ${student.lastName}`;
  const rosterOwns = (...fields: string[]) =>
    student.source === "roster" &&
    fields.some((field) => readOnlyFields?.includes(field));
  const ageOwned = student.birthDate
    ? rosterOwns("birthDate")
    : rosterOwns("ageOverride");
  const rosterNote =
    rosterOwns("benchmarkSex", "externalId", "gradeLevel", "firstName") ||
    ageOwned;
  const testDay = dateParts(testDate);
  const onTestDay =
    (ageOnTestDay !== undefined && ageOnTestDay !== null) || testDay !== null;
  const age =
    ageOnTestDay ??
    (testDay
      ? (ageOn(student.birthDate, testDay) ?? student.ageOverride)
      : (student.ageOverride ?? ageOn(student.birthDate, today())));
  const notSet = t("review.notSet", { defaultValue: "Not set" });
  const levelLabel = (l: ModificationLevel) =>
    t(`modification.short.${l}`, { defaultValue: LEVEL_SHORT[l] });
  const noteMissing = level !== "none" && !plannedNote.trim();
  const hasHealth = flags.length > 0 || !!notes.trim();
  const subtitle = [
    student.gradeLevel
      ? t("classes.gradeLabel", {
          defaultValue: "Grade {{grade}}",
          grade: student.gradeLevel,
        })
      : undefined,
    classLabel,
  ]
    .filter(Boolean)
    .join(" · ");

  const submit = async () => {
    setError(null);
    try {
      const saved = await save({
        studentId: student.id,
        classId,
        body: {
          notes: notes.trim() || null,
          plannedModification: level,
          plannedEventId: level === "alternative" ? eventId || null : null,
          plannedNote: level === "none" ? null : plannedNote.trim() || null,
          healthFlags: flags,
        },
      }).unwrap();
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setError(
        messageOf(
          e,
          t("classes.accommodationSaveError", {
            defaultValue: "Could not save the accommodation plan",
          }),
        ),
      );
    }
  };

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={name}
      subtitle={subtitle}
      width={480}
      titleAdornment={
        <Box
          aria-hidden="true"
          data-testid="student-initials"
          sx={(theme) => ({
            width: 52,
            height: 52,
            flexShrink: 0,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: theme.palette.primary.main,
            color: theme.palette.tokens.onPrimary,
            fontSize: 17,
            fontWeight: 800,
          })}
        >
          {initialsOf(student.firstName, student.lastName)}
        </Box>
      }
      footer={
        canEdit ? (
          <Box sx={{ display: "flex", gap: 1.25, justifyContent: "flex-end" }}>
            <Button color="inherit" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              disabled={noteMissing || saving || !loaded}
              onClick={submit}
            >
              {t("classes.savePlan", { defaultValue: "Save plan" })}
            </Button>
          </Box>
        ) : undefined
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
        <Box
          component="section"
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.5,
          }}
        >
          {rosterNote ? (
            <Typography
              sx={(theme) => ({
                gridColumn: "1 / -1",
                fontSize: 13,
                color: theme.palette.tokens.textMuted,
              })}
            >
              {t("student.rosterManaged")}
            </Typography>
          ) : null}
          <Field
            label={t("classes.benchmarkTable")}
            roster={rosterOwns("benchmarkSex")}
          >
            {student.benchmarkSex ?? notSet}
          </Field>
          <Field
            roster={ageOwned}
            label={
              onTestDay
                ? t("classes.ageOnTestDay", {
                    defaultValue: "Age on test day",
                  })
                : t("classes.age")
            }
          >
            {age ?? notSet}
          </Field>
          <Field
            label={t("classes.sisId", { defaultValue: "SIS id" })}
            roster={rosterOwns("externalId")}
          >
            {student.externalId ?? "—"}
          </Field>
          <Field label={t("classes.sourceLabel", { defaultValue: "Source" })}>
            {student.source ? (
              <StatusChip
                status={student.source === "roster" ? "connected" : "manual"}
              />
            ) : (
              "—"
            )}
          </Field>
        </Box>

        {showHealth ? (
          <>
            {canEdit && loadFailed ? (
              <InlineAlert severity="error">
                {t("classes.accommodationLoadError", {
                  defaultValue:
                    "Could not load the accommodation plan. Saving is off until it loads.",
                })}
              </InlineAlert>
            ) : null}
            <Box
              component="section"
              sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <SectionHeading>
                  {t("classes.accommodationPlan", {
                    defaultValue: "Accommodation plan",
                  })}
                </SectionHeading>
                <Typography
                  component="span"
                  sx={(theme) => ({
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: theme.palette.tokens.textMuted,
                  })}
                >
                  {t("classes.accommodationAccess", {
                    defaultValue:
                      "Visible to staff with accommodation access. Volunteer scorers never see health notes.",
                  })}
                </Typography>
              </Box>
              {canEdit ? (
                <Box
                  sx={{
                    "& .MuiToggleButtonGroup-root": {
                      flexWrap: "wrap",
                      gap: 0.75,
                    },
                  }}
                >
                  <FilterPills
                    options={LEVELS.map((l) => ({
                      value: l,
                      label: levelLabel(l),
                    }))}
                    value={level}
                    onChange={(next) => setLevel(next as ModificationLevel)}
                    aria-label={t("modification.label")}
                  />
                </Box>
              ) : (
                <Typography sx={{ fontWeight: 700 }}>
                  {levelLabel(level)}
                </Typography>
              )}
              {level === "alternative" ? (
                canEdit ? (
                  <Box
                    sx={{
                      "& .MuiToggleButtonGroup-root": {
                        flexWrap: "wrap",
                        gap: 0.75,
                      },
                    }}
                  >
                    <FilterPills
                      size="sm"
                      options={ALTERNATIVES.map((e) => ({
                        value: e,
                        label: t(`events.${e}`, {
                          defaultValue: EVENT_LABEL[e],
                        }),
                      }))}
                      value={eventId}
                      onChange={setEventId}
                      aria-label={t("classes.alternativeEvent", {
                        defaultValue: "Alternative event",
                      })}
                    />
                  </Box>
                ) : (
                  <Typography>
                    {eventId
                      ? t(`events.${eventId}`, {
                          defaultValue:
                            EVENT_LABEL[eventId as EventId] ?? eventId,
                        })
                      : notSet}
                  </Typography>
                )
              ) : null}
              {level !== "none" ? (
                canEdit ? (
                  <TextField
                    label={t("classes.plannedNote", {
                      defaultValue: "Planned change (shown at the station)",
                    })}
                    placeholder={t("classes.plannedNotePlaceholder", {
                      defaultValue: "Planned change, e.g. incline push-up",
                    })}
                    value={plannedNote}
                    onChange={(e) => setPlannedNote(e.target.value)}
                    inputProps={{ maxLength: 200 }}
                    error={noteMissing}
                    helperText={`${plannedNote.length}/200`}
                    required
                    fullWidth
                  />
                ) : (
                  <Typography>{plannedNote || notSet}</Typography>
                )
              ) : null}
              <Typography
                sx={(theme) => ({
                  fontSize: 12,
                  lineHeight: 1.4,
                  color: theme.palette.tokens.textMuted,
                })}
              >
                {t("classes.scorerVisibility", {
                  defaultValue:
                    "Volunteer scorers see the planned modification at their station. They never see health notes.",
                })}
              </Typography>
            </Box>

            {canEdit || hasHealth ? (
              <Box
                component="section"
                data-testid="health-note"
                sx={(theme) => ({
                  px: 2,
                  py: 1.75,
                  borderRadius: `${radius.panel}px`,
                  bgcolor: theme.palette.dangerSoft,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                })}
              >
                <Typography
                  component="span"
                  sx={(theme) => ({
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: theme.palette.dangerInk,
                  })}
                >
                  {t("classes.healthNote", { defaultValue: "Health note" })}
                </Typography>
                {canEdit ? (
                  <>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", columnGap: 1 }}
                    >
                      {FLAGS.map((f) => (
                        <FormControlLabel
                          key={f}
                          control={
                            <Checkbox
                              checked={flags.includes(f)}
                              onChange={(e) =>
                                setFlags(
                                  e.target.checked
                                    ? [...flags, f]
                                    : flags.filter((x) => x !== f),
                                )
                              }
                            />
                          }
                          label={t(`classes.healthFlag.${f}`, {
                            defaultValue: FLAG_LABEL[f],
                          })}
                        />
                      ))}
                    </Box>
                    <TextField
                      label={t("classes.healthNotes", {
                        defaultValue:
                          "Notes (IEP/504 context, never shown to scorers)",
                      })}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      multiline
                      minRows={2}
                      inputProps={{ maxLength: 500 }}
                      helperText={`${notes.length}/500`}
                      fullWidth
                    />
                  </>
                ) : (
                  <>
                    {flags.length ? (
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        {flags
                          .map((f) =>
                            t(`classes.healthFlag.${f}`, {
                              defaultValue: FLAG_LABEL[f],
                            }),
                          )
                          .join(" · ")}
                      </Typography>
                    ) : null}
                    {notes.trim() ? (
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        {notes}
                      </Typography>
                    ) : null}
                  </>
                )}
              </Box>
            ) : null}
          </>
        ) : null}

        <Box
          component="section"
          sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
        >
          <SectionHeading>
            {t("classes.testingHistory", { defaultValue: "Testing history" })}
          </SectionHeading>
          {history.length ? (
            <Box
              component="ul"
              sx={{
                listStyle: "none",
                m: 0,
                p: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {history.map((h) => (
                <Box
                  component="li"
                  key={`${h.window}-${h.tier}`}
                  sx={(theme) => ({
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    px: 1.75,
                    py: 1.5,
                    borderRadius: `${radius.control}px`,
                    border: `1px solid ${theme.palette.divider}`,
                  })}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    {h.window}
                  </Typography>
                  <AwardChip award={h.tier} size="sm" />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              sx={(theme) => ({
                fontSize: 13,
                color: theme.palette.tokens.textMuted,
              })}
            >
              {t("classes.noHistory", {
                defaultValue: "No finalized tests yet.",
              })}
            </Typography>
          )}
          {onOpenResult && history.length ? (
            <Button
              variant="text"
              onClick={onOpenResult}
              sx={{ alignSelf: "flex-start", px: 0 }}
            >
              {t("classes.openResult", {
                defaultValue: "Open {{window}} result →",
                window: history[0].window.split(" ")[0].toLowerCase(),
              })}
            </Button>
          ) : null}
        </Box>

        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      </Box>
    </SideDrawer>
  );
};

export default StudentRecordDrawer;
